import { FC, useContext, useMemo, useState } from 'react'
import { EdgeLabelRenderer, EdgeProps, getSmoothStepPath } from 'reactflow'
import { ADD_NEXT_FALSE_PREFIX_ID, ADD_NEXT_PREFIX_ID, ADD_NEXT_TRUE_PREFIX_ID } from '../Settings'
import PlusButton from './PlusButton'
import { getLineColor } from './common'
import EdgeLabel from './EdgeLabel'
import { EventActionNodeType } from '../types'
import { CampaignContext, ICampaignContext } from '@/expr/Context'
import { ConditionNode, PointValue, SplitNode } from '@/api/dashboard'

export const DefaultEdge: FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
}) => {
  const [hovered, setHovered] = useState(false)

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  return (
    <>
      <path
        onMouseOver={() => setHovered(true)}
        onMouseOut={() => setHovered(false)}
        id={id}
        style={{
          stroke: getLineColor(),
          strokeWidth: data.style.strokeWidth ? data.style.strokeWidth : data.selected ? '3' : '2',
          strokeOpacity: data.style.strokeOpacity,
        }}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        fillRule="evenodd"
      />

      <EdgeLabelRenderer>
        <PlusButton
          visible={hovered}
          id={ADD_NEXT_PREFIX_ID + data.sourceId}
          onClick={(target: HTMLDivElement) => data.addClick(target)}
          transform={`translate(-50%, -50%) translate(${labelX}px,${labelY}px)`}
        />
      </EdgeLabelRenderer>
    </>
  )
}

const getPercent = (nodeVisits: PointValue[] | undefined, nodeId: string, childNodeId: string) => {
  if (!nodeVisits) {
    return null
  }

  const thisNodeVisits = nodeVisits?.find(it => it.category == nodeId)
  if (thisNodeVisits?.value && nodeVisits) {
    const stat = nodeVisits.find(it => it.category == childNodeId) || { value: 0 }
    return Math.round((100 * stat.value!) / thisNodeVisits.value!)
  }
  return null
}

export const RightEdge: FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
}) => {
  const [hovered, setHovered] = useState(false)
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 16,
  })
  const context = useContext(CampaignContext) as ICampaignContext

  const text = useMemo(() => {
    if (data.sourceNode.model_type == EventActionNodeType.ConditionNode) {
      const node = data.sourceNode as ConditionNode
      const percent = getPercent(context?.nodeAnalytics?.nodeVisits, node.id, node.true_node_id!)

      if (percent !== null) {
        return (
          <>
            TRUE <span className="text-text-brand-primary"> ({percent}%)</span>
          </>
        )
      }
      return 'TRUE'
    }

    if (data.sourceNode.model_type == EventActionNodeType.SplitNode) {
      const node = data.sourceNode as SplitNode
      const percent = 100 - data.sourceNode.distribution[0].value
      const percentVisits = getPercent(
        context?.nodeAnalytics?.nodeVisits,
        node.id,
        data.sourceNode.distribution[0].next_node_id!,
      )

      if (percentVisits !== null) {
        return (
          <>
            {percent}% <span className="text-text-brand-primary"> ({percentVisits}%)</span>
          </>
        )
      }

      return percent + '%'
    }

    return ''
  }, [context.nodeAnalytics?.nodeVisits])

  let plusButtonX = labelX + 70
  let plusButtonY = labelY

  if (sourceY - targetY > 10) {
    plusButtonX = labelX
    plusButtonY = labelY + 25
  } else if (Math.abs(sourceX - targetX) < 25) {
    plusButtonX = labelX
    plusButtonY = labelY + 25
  }

  return (
    <>
      <path
        id={id}
        onMouseOver={() => setHovered(true)}
        onMouseOut={() => setHovered(false)}
        style={{
          stroke: getLineColor(),
          strokeWidth: data.style.strokeWidth ? data.style.strokeWidth : data.selected ? '3' : '2',
          strokeOpacity: data.style.strokeOpacity,
        }}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        fillRule="evenodd"
      />

      <EdgeLabelRenderer>
        <EdgeLabel labelX={labelX} labelY={labelY} value={text} />
        <PlusButton
          visible={hovered}
          id={ADD_NEXT_PREFIX_ID + ADD_NEXT_TRUE_PREFIX_ID + data.sourceId}
          onClick={target => data.addClick(target, true)}
          transform={`translate(-50%, -50%) translate(${plusButtonX}px,${plusButtonY}px)`}
        />
      </EdgeLabelRenderer>
    </>
  )
}

export const LeftEdge: FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
}) => {
  const context = useContext(CampaignContext) as ICampaignContext
  const [hovered, setHovered] = useState(false)
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 16,
  })

  const text = useMemo(() => {
    if (data.sourceNode.model_type == EventActionNodeType.ConditionNode) {
      const node = data.sourceNode as ConditionNode
      const percent = getPercent(context?.nodeAnalytics?.nodeVisits, node.id, node.true_node_id!)
      if (percent !== null) {
        return (
          <>
            FALSE <span className="text-text-brand-primary"> ({100 - percent}%)</span>
          </>
        )
      }
      return 'FALSE'
    }

    if (data.sourceNode.model_type == EventActionNodeType.SplitNode) {
      const node = data.sourceNode as SplitNode
      const percent = data.sourceNode.distribution[0].value
      const percentVisits = getPercent(
        context?.nodeAnalytics?.nodeVisits,
        node.id,
        data.sourceNode.distribution[0].next_node_id!,
      )

      if (percentVisits !== null) {
        return (
          <>
            {percent}% <span className="text-text-brand-primary"> ({percentVisits}%)</span>
          </>
        )
      }

      return percent + '%'
    }

    return ''
  }, [context.nodeAnalytics?.nodeVisits])

  let plusButtonX = labelX - 70
  let plusButtonY = labelY

  if (sourceY - targetY > 55) {
    plusButtonX = labelX
    plusButtonY = labelY + 70
  } else if (Math.abs(sourceX - targetX) < 55) {
    plusButtonX = labelX
    plusButtonY = labelY + 70
  }

  return (
    <>
      <path
        onMouseOver={() => setHovered(true)}
        onMouseOut={() => setHovered(false)}
        id={id}
        style={{
          stroke: getLineColor(),
          strokeWidth: data.style.strokeWidth ? data.style.strokeWidth : data.selected ? '3' : '2',
          strokeOpacity: data.style.strokeOpacity,
        }}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        fillRule="evenodd"
      />

      <EdgeLabelRenderer>
        <EdgeLabel labelX={labelX} labelY={labelY} value={text} />
        <PlusButton
          visible={hovered}
          id={ADD_NEXT_PREFIX_ID + ADD_NEXT_FALSE_PREFIX_ID + data.sourceId}
          onClick={target => data.addClick(target, false)}
          transform={`translate(-50%, -50%) translate(${plusButtonX}px,${plusButtonY}px)`}
        />
      </EdgeLabelRenderer>
    </>
  )
}
